<template>
    <app-layout v-if="isPageReady">
        <common-content-spacer height="medium">
            <common-content-spacer height="small">
                <common-breadcrumbs :breadcrumbs="pageBreadcrumbs"></common-breadcrumbs>

                <common-page-header>
                    <template #heading>
                        <div class="page-header__wrapper">
                            <span>{{ pageHeader }}</span>
                            <div v-if="!$auth.isPracticeAncillary()" class="page-header__link">
                                <router-link
                                    :to="{
                                        name: 'profile.user',
                                        params: {
                                            id: userId,
                                        },
                                    }"
                                >
                                    {{ $t('pages.profile.preview.preview_profile_link') }}
                                    <common-icon name="open-link" size="small"></common-icon>
                                </router-link>
                            </div>
                        </div>
                    </template>
                </common-page-header>
            </common-content-spacer>

            <common-tabs ref="tabsRef" :tabs="tabs" :activeTab="tabs[0].name">
                <template v-slot:account>
                    <common-content-wrapper class="form-padding" width="gigantic">
                        <common-content-spacer height="large">
                            <profile-photo-upload
                                ref="profilePhotoUpload"
                                :previewPhoto="$auth.getActiveMember().avatar.source"
                                @save-photo="savePhoto"
                                @remove-photo="removePhoto"
                            ></profile-photo-upload>
                            <account-information-form
                                @account-information-saved="accountInformationSaved"
                            ></account-information-form>
                        </common-content-spacer>
                    </common-content-wrapper>
                </template>
                <template v-slot:professional>
                    <common-content-wrapper class="form-padding" width="gigantic">
                        <profile-professional-form
                            :states="states"
                            :specialities="specialities"
                            :physicianGroups="physicianGroups"
                        ></profile-professional-form>
                    </common-content-wrapper>
                </template>
            </common-tabs>
        </common-content-spacer>
    </app-layout>
</template>
<script>
    import PageWithDataMixin from 'mixins/PageWithDataMixin'
    import AppLayout from 'components/layouts/AppLayout'
    import ProfileProfessionalForm from 'components/forms/profile/ProfileProfessionalForm'
    import AccountInformationForm from 'components/forms/profile/AccountInformationForm'
    import ProfilePhotoUpload from 'components/page-elements/profile/ProfilePhotoUpload'
    import StatesAPI from 'api/StatesAPI'
    import SpecialitiesAPI from 'api/SpecialitiesAPI'
    import PhysicianGroupsAPI from 'api/PhysicianGroupsAPI'
    import UsersAPI from 'api/UsersAPI'
    import { updateStoreMembers, updateCurrentUser } from '~/utils'

    export default {
        mixins: [PageWithDataMixin],

        components: {
            AppLayout,
            ProfileProfessionalForm,
            AccountInformationForm,
            ProfilePhotoUpload,
        },

        data() {
            return {
                states: [],
                specialities: [],
                physicianGroups: [],
            }
        },

        loadData() {
            return {
                states: StatesAPI.getList(),
                specialities: SpecialitiesAPI.getListMultiselect(),
                physicianGroups: PhysicianGroupsAPI.getList(),
            }
        },

        computed: {
            tabs() {
                const tabs = [
                    {
                        name: 'account',
                        displayName: this.$t('pages.profile.professional.tabs.account'),
                        icon: 'user-profile',
                    },
                ]
                if (!this.$auth.isPracticeAncillary()) {
                    tabs.push({
                        name: 'professional',
                        displayName: this.$t('pages.profile.professional.tabs.professional'),
                        icon: 'stethoscope',
                    })
                }
                return tabs
            },
            pageHeader() {
                const activeMember = this.$auth.getActiveMember()
                const user = this.$auth.getUser()

                if (user.id === activeMember.id) {
                    return this.$t('elements.profile.professional.your_profile')
                }

                let physicianName = `${activeMember.firstName} ${activeMember.lastName}`
                if (activeMember.designation) {
                    physicianName += ` ${activeMember.designation}`
                }
                return this.$t('elements.profile.professional.header_text', { physicianName })
            },

            pageBreadcrumbs() {
                return [
                    {
                        label: this.$t('pages.profile.professional.breadcrumbs.home'),
                        route: { name: 'index.home' },
                    },
                    {
                        label: this.$t('pages.profile.professional.breadcrumbs.manage_account'),
                    },
                ]
            },

            userId() {
                return this.$auth.getActiveMember().id
            },
        },

        methods: {
            savePhoto(file) {
                return UsersAPI.uploadAvatar(this.$auth.getActiveMember().id, file).then(() => {
                    Promise.all([updateStoreMembers(), setTimeout(() => updateCurrentUser(), 2500)]).then(() => {
                        this.$refs.profilePhotoUpload.resetPhotoUploadModal()
                    })
                })
            },

            removePhoto() {
                return UsersAPI.removeAvatar(this.$auth.getActiveMember().id).then(() => {
                    Promise.all([updateStoreMembers(), updateCurrentUser()]).then(() => {
                        this.$refs.profilePhotoUpload.resetPhotoUploadModal(true)
                    })
                })
            },

            accountInformationSaved() {
                if (this.$auth.isPracticeAncillary()) {
                    this.$router.push({ name: 'index.home' })
                    return
                }

                this.$refs.tabsRef.selectedTab = this.tabs[1].name
            },
        },
    }
</script>

<style lang="scss" scoped>
    .form-padding {
        padding-top: 60px;
    }
</style>
